import React from 'react';
import { instruktørData } from '../data';
import { getResizedImageUrl, getImageSrcSet } from '../lib/utils';
import SEO from '../components/SEO';

const instruktoererSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": "https://moonyoga-fredericia.dk/instruktoerer/#webpage",
      "url": "https://moonyoga-fredericia.dk/instruktoerer",
      "name": "Mød vores instruktører | Moon Yoga Fredericia",
      "description": "Mød Camilla og Jozee, dine instruktører hos Moon Yoga Fredericia. Vi skaber et trygt, hjertevarmt og nærværende rum for din yogapraksis."
    },
    {
      "@type": "Person",
      "name": "Jozee",
      "jobTitle": "Yogalærer",
      "worksFor": {
        "@type": "SportsActivityLocation",
        "name": "Moon Yoga Fredericia",
        "url": "https://moonyoga-fredericia.dk"
      },
      "image": "https://assets.auxodev.dk/assets/8bfce4c8-263d-41f5-85ff-0c7b7ca56872/img-0049-red.png"
    },
    {
      "@type": "Person",
      "name": "Camilla",
      "jobTitle": "Yogalærer",
      "worksFor": {
        "@type": "SportsActivityLocation",
        "name": "Moon Yoga Fredericia",
        "url": "https://moonyoga-fredericia.dk"
      },
      "image": "https://assets.auxodev.dk/assets/8bfce4c8-263d-41f5-85ff-0c7b7ca56872/3069.jpg"
    }
  ]
};

export default function Instruktoerer() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-16">
      <SEO
        title="Mød vores dygtige instruktører | Moon Yoga Fredericia"
        description="Mød Camilla og Jozee, dine instruktører hos Moon Yoga Fredericia. Vi skaber et trygt, hjertevarmt og nærværende rum for din yogapraksis. Læs vores historier her."
        path="/instruktoerer"
        schema={instruktoererSchema}
      />
      
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <span className="text-xs sm:text-sm tracking-[0.3em] uppercase text-brand-primary font-medium block">
          Mød os på måtten
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary font-semibold tracking-wide">
          {instruktørData.title}
        </h1>
        <p className="text-base sm:text-lg text-brand-muted leading-relaxed">
          {instruktørData.intro}
        </p>
        <div className="inline-block h-1 w-16 bg-brand-primary/30 rounded-full" />
      </div>

      {/* Instructors Grid - Showing both equally */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        {instruktørData.list.map((instruktor) => (
          <div 
            key={instruktor.id} 
            className="flex flex-col space-y-6 bg-brand-surface border border-brand-primary/5 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300"
          >
            {/* Image Container */}
            <div className="relative overflow-hidden aspect-[4/5] rounded-2xl shadow-sm border border-brand-primary/10 bg-brand-light-gold">
              <img
                src={getResizedImageUrl(instruktor.image, "width=600,quality=85,format=auto")}
                srcSet={getImageSrcSet(instruktor.image, [300, 600, 900, 1200])}
                sizes="(max-width: 768px) 100vw, 50vw"
                alt={`${instruktor.name} - Moon Yoga Fredericia Instruktør`}
                className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
                loading="lazy"
                width="400"
                height="500"
              />
              {/* Soft warm overlay */}
              <div className="absolute inset-0 bg-brand-primary/5 pointer-events-none" />
              
              {instruktor.isPlaceholder && (
                <div className="absolute inset-0 bg-brand-light-gold/90 flex flex-col items-center justify-center p-6 text-center">
                  <div className="h-16 w-16 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center mb-3">
                    <i className="fa-light fa-user-plus text-3xl" />
                  </div>
                  <span className="text-xs uppercase tracking-widest text-brand-primary font-semibold">
                    Ny instruktør på vej
                  </span>
                </div>
              )}
            </div>

            {/* Content Container */}
            <div className="space-y-4 flex-1 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="space-y-1">
                  <span className="text-xs uppercase tracking-widest text-brand-primary font-semibold block">
                    {instruktor.role}
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl text-brand-text font-semibold tracking-wide">
                    {instruktor.name}
                  </h2>
                </div>

                {/* Clear Internal Placeholder for Bio Text (Rule 5) */}
                {instruktor.placeholderText && (
                  <div className="p-4 rounded-xl bg-brand-primary/5 border border-brand-primary/10">
                    <span className="block text-[10px] uppercase tracking-widest text-brand-primary/60 font-semibold mb-1">
                      Intern placeholder
                    </span>
                    <p className="text-xs font-mono text-brand-muted leading-relaxed">
                      {instruktor.placeholderText}
                    </p>
                  </div>
                )}

                {/* Bio Text */}
                <div className="space-y-3 text-brand-text/90 text-sm sm:text-base leading-relaxed">
                  {instruktor.id === 'camilla' && (
                    <p className="font-medium text-brand-primary italic">
                      "For Camilla handler yoga ikke om at blive et bedre menneske, men om at huske den, du allerede er."
                    </p>
                  )}
                  <p 
                    className="whitespace-pre-line"
                    dangerouslySetInnerHTML={{ __html: instruktor.fallbackBio }}
                  />
                </div>
              </div>

              {/* Core Values / Highlights */}
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-brand-primary/5">
                <div className="p-3 rounded-xl bg-brand-light-gold/30 border border-brand-primary/5 flex gap-2.5 items-start">
                  <i className="fa-solid fa-sparkles text-brand-primary text-sm mt-0.5" />
                  <div>
                    <h3 className="font-serif font-semibold text-xs text-brand-text">Små, intime hold</h3>
                    <p className="text-[10px] text-brand-muted mt-0.5">Tid og plads til dig og din krop.</p>
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-brand-light-gold/30 border border-brand-primary/5 flex gap-2.5 items-start">
                  <i className="fa-solid fa-leaf text-brand-primary text-sm mt-0.5" />
                  <div>
                    <h3 className="font-serif font-semibold text-xs text-brand-text">Cyklisk praksis</h3>
                    <p className="text-[10px] text-brand-muted mt-0.5">Inspireret af naturens rytmer.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
